/**
 * Which screen an *enlisted* pilot is looking at.
 *
 * Split out of `career-surface.ts` when G31 gave REQUEST TRANSFER a screen:
 * that class is the store-facing half of `/career` and this is the part of it
 * that decides nothing and touches nothing — a pure `(view, save, model) →
 * ShellScreen` map. Keeping it pure is also what lets a test render every
 * career screen without a `CareerStore`.
 *
 * The screens before enlistment (CAREER MENU, the roster, TOP ACES, the
 * enlistment dialog) stay in the surface, because they are chosen from the
 * saves list rather than from a save.
 */

import {
  aircraftLabel,
  DAYS_PER_MONTH,
  personalAircraftRequest,
  simendOutcome,
  simendSequenceOf,
  transferOptions,
  type CareerState,
  type MissionSpec,
} from "@red-opus/engine";
import type { CareerSave } from "../../career/career-store.ts";
import { PAINT_SWATCHES } from "../../career/career-paint.ts";
import { pilotHeadline, dutyLine, pilotSheet } from "../../career/career-view.ts";
import type { FontId } from "../render/draw-ops.ts";
import type { FontAtlas } from "../render/text-metrics.ts";
import type { CareerModel } from "./career-model.ts";
import { backupScreen } from "./career-backup-screen.ts";
import { aerodromeScreen, pilotRecordScreen, transferScreen } from "./career-screens.ts";
import { debriefingScreen } from "./debrief-screens.ts";
import { outcomeScreen, simendSteps, type SimendStep } from "./simend-screens.ts";
import type { MenuAction } from "./menu-model.ts";
import { missionAssignmentScreen } from "./missions-screens.ts";
import { flightAssignmentsScreen } from "./flight-assignments-screen.ts";
import { paintMenuScreen, paintPartScreen, personalAircraftScreen, requestAircraftScreen } from "./paint-screens.ts";
import type { ActionSpec } from "./panel-parts.ts";
import type { ShellScreen } from "./shell-screen.ts";

const command = (name: string): MenuAction => ({ kind: "command", command: name });

export const AERODROME_BACKDROP: Readonly<Record<string, string>> = {
  allied: "screens/B-AERO.SCR",
  german: "screens/G-AERO.SCR",
};

/**
 * The `SIMEND.DAT` sequence this sortie earned, page by page.
 *
 * The career door has two facts `/fly` does not: the campaign's own casualty
 * roll, which decides the family of outcome (hospital, prison camp, killed),
 * and how long it cost, which is what `%t` stands for. Both come off
 * `lastConsequence`; the eight landing predicates come off the debriefing.
 */
function outcomeSteps(save: CareerSave): readonly SimendStep[] {
  const debrief = save.lastDebrief;
  if (!debrief) return [];
  const casualty = save.lastConsequence?.casualty;
  const outcome = simendOutcome({ ...debrief, ...(casualty ? { casualty: casualty.kind } : {}) });
  const months = casualty ? casualty.lostDays / DAYS_PER_MONTH : undefined;
  // `%t` is the time in hospital or in the camp; `%a` is whoever shot him down,
  // which the debriefing has carried since the sortie was flown. A save written
  // before it did simply leaves `%a` unfilled, and `simendOutcome` picks the
  // unnamed VANQUISHED! for it — which is what those saves already showed.
  const facts = {
    ...(months === undefined ? {} : { months }),
    ...(debrief.killedBy === undefined ? {} : { killedBy: debrief.killedBy }),
  };
  return simendSteps(simendSequenceOf(outcome, facts), save.career.nation);
}

export function aerodromeBackdropOf(career: CareerState): string {
  return AERODROME_BACKDROP[career.nation] ?? "screens/B-AERO.SCR";
}

/**
 * The button rows the two briefing screens carry. Constants rather than
 * arguments: they never vary, and the surface used to inject them only because
 * this file predates it.
 */
/** `PS.EXE` 257487's three — see `missions-surface.ts` for why `START POSITION` is not a fourth. */
const ASSIGNMENT_ACTIONS: readonly ActionSpec[] = [
  { id: "cancel", label: "CANCEL", action: command("aerodrome") },
  { id: "flight", label: "FLIGHT ASSIGNMENTS", action: command("flight") },
  { id: "begin", label: "BEGIN MISSION", action: command("begin") },
];

const FLIGHT_ACTIONS: readonly ActionSpec[] = [
  { id: "assignment", label: "MISSION ASSIGNMENT", action: command("assignment") },
  { id: "realism", label: "REALISM PANEL", action: command("realism") },
  { id: "begin", label: "BEGIN MISSION", action: command("begin") },
];

/** Everything the router needs that it cannot read off the model. */
export interface PilotScreenContext {
  readonly model: CareerModel;
  readonly save: CareerSave;
  readonly fonts: ReadonlyMap<FontId, FontAtlas>;
  /** The sortie waiting at the aerodrome, when the store could produce one. */
  readonly spec: MissionSpec | undefined;
}

function atlas(fonts: ReadonlyMap<FontId, FontAtlas>, id: FontId): FontAtlas {
  const found = fonts.get(id);
  if (!found) throw new Error(`shell: font atlas ${id} was never loaded`);
  return found;
}

/**
 * PERSONAL AIRCRAFT and the paint shop. A pilot with no machine of his own only
 * ever sees the request list — the paint screens are unreachable until he has
 * one, which is exactly the original's gate (there is no separate paint
 * permission; `paintEligibility` records the evidence).
 */
function paintScreen(context: PilotScreenContext, font: FontAtlas, heading: FontAtlas): ShellScreen {
  const { model, save } = context;
  const held = save.career.personalAircraft;
  if (model.view === "requestAircraft" || !held) {
    const request = personalAircraftRequest(save.career);
    return requestAircraftScreen(
      model,
      request.offered.map((key) => ({ id: key, label: aircraftLabel(key) })),
      request.message,
      // The message is FONT3 prose; the row is FONT1. It used to get FONT1 for
      // both, which wrapped the prose to the wrong column.
      font,
      heading,
    );
  }
  if (model.view === "paintPart") return paintPartScreen(heading);
  if (model.view === "paintColour") {
    return paintMenuScreen(model.paintPart, PAINT_SWATCHES.map((swatch) => swatch.index), heading);
  }
  return personalAircraftScreen(
    {
      aircraft: held.aircraft,
      wings: held.wings,
      fuselage: held.fuselage,
      canRequest: personalAircraftRequest(save.career).allowed,
      background: aerodromeBackdropOf(save.career),
    },
    font,
    heading,
  );
}

const PAINT_VIEWS: readonly CareerModel["view"][] = ["aircraft", "paintPart", "paintColour", "requestAircraft"];

export function pilotScreen(context: PilotScreenContext): ShellScreen {
  const { model, save, fonts } = context;
  const font = atlas(fonts, "FONT3");
  const heading = atlas(fonts, "FONT1");
  const view = model.view;
  if (view === "record") return pilotRecordScreen(save.career, pilotSheet(save.career), font, heading);
  if (view === "backup") {
    return backupScreen(
      { name: model.name, background: aerodromeBackdropOf(save.career), emblem: save.career.nation === "german" ? "cross" : "roundel" },
      heading,
    );
  }
  if (view === "transfer") return transferScreen(model, transferOptions(save.career), font, heading);
  if (view === "safeReturn") {
    // The outcome sequence, one CONTINUE per page. The last page's CONTINUE
    // leads to the debriefing — which is where the single hardcoded
    // `SAFE RETURN...` used to lead from, so the door out is unchanged.
    const steps = outcomeSteps(save);
    const page = Math.min(model.outcomePage, Math.max(steps.length - 1, 0));
    const step = steps[page];
    if (step) {
      const more = page + 1 < steps.length;
      return outcomeScreen(step, heading, command(more ? "outcome-next" : "debrief"), save.career.nation);
    }
  }
  if ((view === "debrief" || view === "consequences") && save.lastDebrief) {
    return debriefingScreen(
      save.lastDebrief,
      heading,
      atlas(fonts, "TALK"),
      command("consequences"),
      save.career.nation,
    );
  }
  if (PAINT_VIEWS.includes(view)) return paintScreen(context, font, heading);
  if (context.spec && view === "assignment") {
    return missionAssignmentScreen(context.spec, font, heading, ASSIGNMENT_ACTIONS);
  }
  if (context.spec && view === "flight") {
    return flightAssignmentsScreen(context.spec, font, heading, FLIGHT_ACTIONS);
  }
  return aerodromeScreen(save.career, aerodromeBackdropOf(save.career), [
    pilotHeadline(save.career),
    dutyLine(save.career),
  ]);
}
